import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import type { Filter } from '@/models/Filter';
import ApplyResetWrapper from '@/components/filters/ApplyResetWrapper';
import type { VenueDetailsOptions } from '@/utils/filters/options/getVenueDetailsOptions';
import dynamic from 'next/dynamic';
import Loader from '@/components/filters/Loader';
import { formatMessage } from '@/utils/translationHelper';
import type { Locale } from '@/i18n';

const SuggestedFeatures = dynamic(() => import('@/components/filters/SuggestedFeaturesFilter'), {
  ssr: false,
  loading: () => <Loader />
});

type Props = {
  searchTerm: string;
  appliedFilters: Filter;
  options: VenueDetailsOptions;
  locale: Locale;
  labels: {
    title: string;
    allFilters: string;
    resetAllFilters: string;
    cancelAllFilters: string;
    applyAllFilters: string;
    clear: string;
    apply: string;
  };
  setSortingLabel?: Dispatch<SetStateAction<string>>;
  setSortingType?: Dispatch<SetStateAction<string>>;
  sortOptions?: { label: string; value: string }[];
  sortType?: string;
  sort?: string;
  setSort?: Dispatch<SetStateAction<string>>;
  setIsRfpBannerVisible: (isRfpBannerVisible: boolean) => void;
  testID?: string;
  isSearchExpanded: boolean;
  setIsSearchExpanded: (v: boolean) => void;
};

/**
 * Container for suggested feature
 */
export default function SuggestedFeaturesContainer({
  appliedFilters,
  labels,
  options,
  locale,
  sortOptions,
  sortType,
  sort,
  setSort,
  setSortingType,
  setSortingLabel,
  searchTerm,
  setIsRfpBannerVisible,
  testID,
  isSearchExpanded,
  setIsSearchExpanded
}: Props) {
  const { allFilters, resetAllFilters, cancelAllFilters, applyAllFilters, clear, apply } = labels;
  const [selected, setSelected] = useState<Filter>(appliedFilters);
  const [isOpen, setIsOpen] = useState(false);
  const [filterCount, setFilterCount] = useState<number>(0);

  useEffect(() => {
    setSelected(appliedFilters);

    // Calculating filter count to be used on surface filter
    let count = appliedFilters.AMENITIES?.includes('ENVIRONMENTALLY_FRIENDLY') ? 1 : 0;
    const filteredVenueDetails =
      appliedFilters.VENUE_DETAILS?.filter(v =>
        [
          'INSTANT_BOOK_ENABLED',
          'HAS_3D_DIAGRAMS',
          'PASSKEY_ENABLED',
          'SOCIAL_TABLES_ENABLED',
          'FAST_RESPONDER'
        ].includes(v)
      ).length || 0;
    count += filteredVenueDetails;
    setFilterCount(count);
  }, [appliedFilters]);

  return (
    <ApplyResetWrapper
      setIsSearchExpanded={setIsSearchExpanded}
      isSearchExpanded={isSearchExpanded}
      title={formatMessage(labels.title, locale, {
        count: filterCount
      })}
      selected={selected}
      setSelected={setSelected}
      appliedFilters={appliedFilters}
      filters={['VENUE_DETAILS', 'AMENITIES']}
      testID={testID}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      strings={{ allFilters, resetAllFilters, cancelAllFilters, applyAllFilters, clear, apply }}
      setSortingLabel={setSortingLabel}
      setSortingType={setSortingType}
      sortOptions={sortOptions}
      sortType={sortType}
      sort={sort}
      setSort={setSort}
      searchTerm={searchTerm}
      filterCount={filterCount}
      setIsRfpBannerVisible={setIsRfpBannerVisible}
    >
      {isOpen ? (
        <SuggestedFeatures options={options} selected={selected} setSelected={setSelected} testID={testID} />
      ) : (
        <></>
      )}
    </ApplyResetWrapper>
  );
}
